import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import "./App.css";
import {
  SECRET_DATE,
  SECRET_PASSWORD,
  SECRET_STORAGE_KEY,
} from "./secretConfig";

const MILESTONE_DATES = [
  "03/23/2025",
  "04/23/2025",
  "05/23/2025",
  "06/23/2025",
  "07/23/2025",
  "08/23/2025",
  "09/23/2025",
  "10/23/2025",
  "11/23/2025",
  "12/23/2025",
  "01/23/2026",
  "02/23/2026",
  "03/23/2026",
  "04/23/2026",
  "05/23/2026",
  "06/23/2026",
];

const DAY_COUNTER_START = new Date(2025, 2, 23);

function daysSinceStart() {
  const now = new Date();
  const todayUtc = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const startUtc = Date.UTC(
    DAY_COUNTER_START.getFullYear(),
    DAY_COUNTER_START.getMonth(),
    DAY_COUNTER_START.getDate(),
  );

  return Math.max(0, Math.floor((todayUtc - startUtc) / 86_400_000));
}

function LetterPage() {
  const navigate = useNavigate();
  const [secretModalOpen, setSecretModalOpen] = useState(false);
  const [secretInput, setSecretInput] = useState("");
  const [secretError, setSecretError] = useState("");
  const [secretUnlocked, setSecretUnlocked] = useState(
    () => sessionStorage.getItem(SECRET_STORAGE_KEY) === "1",
  );
  const [daysTogether, setDaysTogether] = useState(daysSinceStart);
  const secretInputRef = useRef(null);

  useEffect(() => {
    let timer;
    const refreshAtMidnight = () => {
      setDaysTogether(daysSinceStart());
      const now = new Date();
      const nextMidnight = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate() + 1,
      );
      timer = window.setTimeout(refreshAtMidnight, nextMidnight - now + 100);
    };

    refreshAtMidnight();
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!secretModalOpen) return;
    const t = requestAnimationFrame(() => secretInputRef.current?.focus());
    const onKey = (e) => {
      if (e.key === "Escape") {
        setSecretModalOpen(false);
        setSecretInput("");
        setSecretError("");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      cancelAnimationFrame(t);
      window.removeEventListener("keydown", onKey);
    };
  }, [secretModalOpen]);

  const openSecretModal = () => {
    setSecretModalOpen(true);
    setSecretError("");
  };

  const closeSecretModal = () => {
    setSecretModalOpen(false);
    setSecretInput("");
    setSecretError("");
  };

  const submitSecret = (e) => {
    e.preventDefault();
    if (secretInput === SECRET_PASSWORD) {
      sessionStorage.setItem(SECRET_STORAGE_KEY, "1");
      setSecretUnlocked(true);
      closeSecretModal();
      navigate("/gallery");
    } else {
      setSecretError("sai oi");
    }
  };

  const flowers = Array.from({ length: 18 }, (_, i) => ({
    id: i,
    delay: Math.random() * 22,
    duration: 16 + Math.random() * 12,
    left: Math.random() * 100,
    size: 18 + Math.random() * 28,
  }));

  return (
    <div className="App">
      <div className="ambient-glow ambient-glow--a" aria-hidden />
      <div className="ambient-glow ambient-glow--b" aria-hidden />

      <div className="flowers-container">
        {flowers.map((flower) => (
          <img
            key={flower.id}
            src={`${process.env.PUBLIC_URL}/740909.png`}
            alt=""
            className="floating-flower"
            style={{
              left: `${flower.left}%`,
              animationDelay: `${flower.delay}s`,
              animationDuration: `${flower.duration}s`,
              width: `${flower.size}px`,
              height: `${flower.size}px`,
            }}
          />
        ))}
      </div>

      <main className="letter-shell">
        <article className="letter-card">
          <header className="letter-header">
            <time className="letter-open-date" dateTime="2025-03-08">
              03 · 08 · 2025
            </time>
          </header>

          <div className="letter-body">
            <p className="letter-salutation">caroline,</p>
            <p>
              i can’t see you physically, so i&apos;m making this cheesy website
              to give you some virtual flowers :)
            </p>
            <p className="letter-highlight">happy women&apos;s day!</p>
            <p className="letter-signoff"> - khoa </p>
          </div>

          <section className="milestones" aria-label="Dates">
            <h2 className="milestones-title">every 23rd</h2>
            <p className="milestones-sub">little checkpoints :)</p>
            <ul className="date-chips">
              {MILESTONE_DATES.map((d) => (
                <li key={d}>
                  {d === SECRET_DATE ? (
                    secretUnlocked ? (
                      <Link
                        to="/gallery"
                        className="date-chip date-chip--unlocked"
                        aria-label={`Open photo gallery for ${d}`}
                      >
                        {d}
                      </Link>
                    ) : (
                      <button
                        type="button"
                        className="date-chip date-chip--secret"
                        onClick={openSecretModal}
                        aria-haspopup="dialog"
                        aria-expanded={secretModalOpen}
                        aria-label={`Unlock gallery for ${d}`}
                      >
                        {d}
                      </button>
                    )
                  ) : (
                    <span className="date-chip">{d}</span>
                  )}
                </li>
              ))}
            </ul>
          </section>

          <section className="day-counter" aria-label="Days since March 23, 2025">
            <span>since 03 · 23 · 2025</span>
            <strong>{daysTogether}</strong>
            <span>days of us</span>
          </section>

          <p className="cap-easter">CAP CAP CAP CAP CAP</p>
        </article>
      </main>

      {secretModalOpen && (
        <div
          className="secret-modal-backdrop"
          role="presentation"
          onClick={closeSecretModal}
        >
          <div
            className="secret-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="secret-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="secret-modal-title" className="secret-modal-title">
              {SECRET_DATE}
            </h3>
            <p className="secret-modal-hint">password?</p>
            <form onSubmit={submitSecret} className="secret-modal-form">
              <input
                ref={secretInputRef}
                type="password"
                className="secret-modal-input"
                value={secretInput}
                onChange={(e) => {
                  setSecretInput(e.target.value);
                  setSecretError("");
                }}
                autoComplete="off"
                aria-invalid={Boolean(secretError)}
                aria-describedby={
                  secretError ? "secret-modal-error" : undefined
                }
              />
              {secretError ? (
                <p
                  id="secret-modal-error"
                  className="secret-modal-error"
                  role="alert"
                >
                  {secretError}
                </p>
              ) : null}
              <div className="secret-modal-actions">
                <button
                  type="button"
                  className="secret-modal-btn secondary"
                  onClick={closeSecretModal}
                >
                  cancel
                </button>
                <button type="submit" className="secret-modal-btn primary">
                  unlock
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default LetterPage;
